import { Link, usePage } from "@inertiajs/react";
import { ReactNode, useState } from "react";
import { DirectionProvider } from "@radix-ui/react-direction";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { User, LogOut, Moon, Sun, Users, Building2, Home, MapPin } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import FlashMessages from "@/components/FlashMessages";
import GlobalErrors from "@/components/GlobalErrors";
import { Toaster } from "@/components/ui/sonner";

interface AdminData {
  email: string;
}

interface PageProps {
  auth?: {
    admin: AdminData;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface LayoutProps {
  children: ReactNode;
}

function AppSidebar() {
  const { auth } = usePage<PageProps>().props;

  // Get initials from email for avatar
  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <Sidebar variant="inset" side="right">
      <SidebarHeader>
        <Link href="/" className="flex items-center gap-2 px-2 py-1.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white dark:bg-black">
            <img src="/icon.svg" alt="Logo" className="h-8 w-8 filter invert dark:invert-0" />
          </div>
          <div className="grid flex-1 text-sm leading-tight">
            <span className="truncate font-semibold">SY Aqar</span>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link href="/desk/users">
                    <Users />
                    <span>المستخدمين</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link href="/desk/agencies">
                    <Building2 />
                    <span>الوكالات العقارية</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link href="/desk/properties">
                    <Home />
                    <span>العقارات</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link href="/desk/regions">
                    <MapPin />
                    <span>المناطق</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton className="w-full">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {auth?.admin ? getInitials(auth.admin.email) : "??"}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{auth?.admin?.email}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent sideOffset={8} align="end" className="w-56">
            <Link href="/desk/admin">
              <DropdownMenuItem>
                <User />
                <span>الحساب الشخصي</span>
              </DropdownMenuItem>
            </Link>
            <DropdownMenuItem
              variant="destructive"
              onSelect={async () => {
                try {
                  await fetch("/admins/sign_out", {
                    method: "DELETE",
                    headers: {
                      "X-CSRF-Token":
                        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
                          ?.content || "",
                    },
                  });
                  window.location.href = "/";
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              <LogOut />
              <span>تسجيل الخروج</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

export default function DeskLayout({ children }: LayoutProps) {
  const [direction] = useState<"ltr" | "rtl">(() => {
    // Detect direction from HTML element on mount
    const dir = document.documentElement.getAttribute("dir") as "ltr" | "rtl";
    return dir || "ltr";
  });
  const { toggleTheme } = useTheme();

  return (
    <DirectionProvider dir={direction}>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
            <SidebarTrigger />
            <div className="flex flex-1 items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="relative"
                aria-label="Toggle theme"
              >
                <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              </Button>
            </div>
          </header>

          <div className="flex flex-1 flex-col gap-4 p-4">
            <Toaster />
            <FlashMessages />
            <GlobalErrors />
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </DirectionProvider>
  );
}
